"use client";

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import type { StudioDashboardWidgetProps } from "@/features/dashboard/hooks/useStudioDashboardWidgetData";
import { useAccessToken, useTenantData } from "@/hooks/shared";
import {
  applyEnrollmentWidgetData,
  type ParsedEnrollmentWidgets,
  parseEnrollmentWidgetsFromWidgetData,
  STUDIO_ENROLLMENT_WIDGET_KEYS,
} from "@/lib/dashboard/enrollment-dashboard-widgets";
import { fetchWidgetDataBatched } from "@/lib/dashboard/widgets-client";
import { QueryKeys } from "@/lib/types";
import { DashboardWidgetId } from "@/lib/types/dashboard-widgets";

const ENROLLMENT_WIDGET_KEY_SET = new Set<string>(
  STUDIO_ENROLLMENT_WIDGET_KEYS,
);

type UseStudioEnrollmentWidgetsDataOptions = {
  regularWidgetIds: DashboardWidgetId[];
  enabled?: boolean;
};

function keysToFetch(
  regularWidgetIds: DashboardWidgetId[],
): DashboardWidgetId[] {
  const requested = new Set(
    regularWidgetIds.filter((id) => ENROLLMENT_WIDGET_KEY_SET.has(id)),
  );
  requested.add(DashboardWidgetId.EnrollmentStatus);
  return STUDIO_ENROLLMENT_WIDGET_KEYS.filter((id) => requested.has(id));
}

export function useStudioEnrollmentWidgetsData({
  regularWidgetIds,
  enabled = true,
}: UseStudioEnrollmentWidgetsDataOptions) {
  const accessToken = useAccessToken();
  const { tenantId } = useTenantData();

  const widgetKeys = useMemo(
    () => keysToFetch(regularWidgetIds),
    [regularWidgetIds],
  );

  const sortedKeySignature = useMemo(
    () => widgetKeys.slice().sort().join("|"),
    [widgetKeys],
  );

  const query = useQuery({
    queryKey: [
      QueryKeys.WidgetData,
      tenantId,
      "enrollment-widgets",
      sortedKeySignature,
    ],
    enabled:
      enabled &&
      widgetKeys.length > 0 &&
      !!accessToken &&
      tenantId != null &&
      tenantId > 0,
    queryFn: async () => {
      const token = accessToken;
      const tid = tenantId;
      if (!token || tid == null) {
        throw new Error("Missing session or tenant for enrollment widgets");
      }
      const items = await fetchWidgetDataBatched(token, tid, widgetKeys);
      return parseEnrollmentWidgetsFromWidgetData(items);
    },
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const parsed: ParsedEnrollmentWidgets = query.data ?? {
    topClasses: [],
    enrollmentStatus: null,
    enrollments: [],
  };

  const mergeWidgetProps = (
    base: StudioDashboardWidgetProps,
  ): StudioDashboardWidgetProps => applyEnrollmentWidgetData(base, parsed);

  return {
    ...query,
    parsed,
    mergeWidgetProps,
  };
}
